import { useCallback, useRef, useState } from "react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/gif", "image/webp"];
const MAX_SIZE = 5 * 1024 * 1024; // 5MB

export interface UseProfilePictureUploadOptions {
  onUploaded?: (objectPath: string) => void;
  onRemoved?: () => void;
}

export interface UseProfilePictureUploadReturn {
  pickFile: () => void;
  removePhoto: () => Promise<void>;
  fileInputProps: {
    ref: React.RefObject<HTMLInputElement>;
    type: "file";
    accept: string;
    className: string;
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  };
  selectedFile: File | null;
  cropOpen: boolean;
  cancelCrop: () => void;
  confirmCrop: (croppedBlob: Blob) => Promise<void>;
  isUploading: boolean;
  isRemoving: boolean;
}

export function useProfilePictureUpload(
  options: UseProfilePictureUploadOptions = {},
): UseProfilePictureUploadReturn {
  const { toast } = useToast();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [cropOpen, setCropOpen] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const pickFile = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const onFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      if (!ALLOWED_TYPES.includes(file.type)) {
        toast({
          title: "Invalid file type",
          description: "Please select an image file (JPEG, PNG, GIF, or WebP)",
          variant: "destructive",
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }
      if (file.size > MAX_SIZE) {
        toast({
          title: "File too large",
          description: "Please select an image under 5MB",
          variant: "destructive",
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
        return;
      }

      // Verify the file decodes as an image before opening the crop dialog so
      // corrupt images can be reported with a clear error.
      const probe = new Image();
      const probeUrl = URL.createObjectURL(file);
      probe.onload = () => {
        URL.revokeObjectURL(probeUrl);
        setSelectedFile(file);
        setCropOpen(true);
      };
      probe.onerror = () => {
        URL.revokeObjectURL(probeUrl);
        toast({
          title: "Invalid image",
          description: "That file doesn't look like a readable image.",
          variant: "destructive",
        });
        if (fileInputRef.current) fileInputRef.current.value = "";
      };
      probe.src = probeUrl;
    },
    [toast],
  );

  const cancelCrop = useCallback(() => {
    setCropOpen(false);
    setSelectedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  }, []);

  const confirmCrop = useCallback(
    async (croppedBlob: Blob) => {
      if (!selectedFile) return;
      setIsUploading(true);
      try {
        const outputType = croppedBlob.type || "image/jpeg";
        const baseName = selectedFile.name.replace(/\.[^.]+$/, "") || "photo";
        const ext = outputType === "image/png" ? "png" : outputType === "image/webp" ? "webp" : "jpg";
        const fileName = `${baseName}-square.${ext}`;

        // Request a presigned URL with one automatic retry — the object
        // storage sidecar occasionally drops a single call (transient 500
        // surfaces as "Failed to generate upload URL"). A short backoff
        // smooths over the blip without making the user click Save again.
        const requestUploadUrl = async () => {
          const r = await apiRequest("POST", "/api/profiles/upload-picture", {
            name: fileName,
            size: croppedBlob.size,
            contentType: outputType,
          });
          return (await r.json()) as { uploadURL: string; objectPath: string };
        };
        let urlPayload: { uploadURL: string; objectPath: string };
        try {
          urlPayload = await requestUploadUrl();
        } catch (firstErr) {
          await new Promise((r) => setTimeout(r, 400));
          try {
            urlPayload = await requestUploadUrl();
          } catch {
            throw new Error(
              "Photo storage is briefly unavailable. Please try again in a moment.",
            );
          }
        }
        const { uploadURL, objectPath } = urlPayload;

        const uploadResponse = await fetch(uploadURL, {
          method: "PUT",
          body: croppedBlob,
          headers: { "Content-Type": outputType },
        });
        if (!uploadResponse.ok) {
          throw new Error(
            "We couldn't upload the photo — please check your connection and try again.",
          );
        }

        await apiRequest("PATCH", "/api/profiles/me", { profilePicture: objectPath });

        queryClient.invalidateQueries({ queryKey: ["/api/profiles/me"] });
        queryClient.invalidateQueries({ queryKey: ["/api/profiles/directory"] });
        queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });

        toast({
          title: "Photo updated",
          description: "Your profile photo has been saved.",
        });

        options.onUploaded?.(objectPath);

        setCropOpen(false);
        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = "";
      } catch (error: any) {
        console.error("Profile picture upload error:", error);
        toast({
          title: "Upload failed",
          description: error?.message || "Failed to upload profile picture",
          variant: "destructive",
        });
      } finally {
        setIsUploading(false);
      }
    },
    [selectedFile, toast, options],
  );

  const removePhoto = useCallback(async () => {
    setIsRemoving(true);
    try {
      await apiRequest("PATCH", "/api/profiles/me", { profilePicture: null });
      queryClient.invalidateQueries({ queryKey: ["/api/profiles/me"] });
      queryClient.invalidateQueries({ queryKey: ["/api/profiles/directory"] });
      queryClient.invalidateQueries({ queryKey: ["/api/admin/users"] });
      toast({
        title: "Photo removed",
        description: "Your profile photo has been removed.",
      });
      options.onRemoved?.();
    } catch (error: any) {
      console.error("Profile picture remove error:", error);
      toast({
        title: "Could not remove photo",
        description: error?.message || "Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsRemoving(false);
    }
  }, [toast, options]);

  return {
    pickFile,
    removePhoto,
    fileInputProps: {
      ref: fileInputRef,
      type: "file",
      accept: "image/jpeg,image/png,image/gif,image/webp",
      className: "hidden",
      onChange: onFileChange,
    },
    selectedFile,
    cropOpen,
    cancelCrop,
    confirmCrop,
    isUploading,
    isRemoving,
  };
}
